import { getCustomerDetail } from '#api/confirmation_forms';
import { IRegisteredLecture } from '#types/registeredLectures';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import styled from 'styled-components';

interface PrevNextLectureProps {
  round: number;
  totalRound: number;
  personId: string;
  confirmationFormId: string;
}

const PrevNextLecture = ({
  round,
  totalRound,
  personId,
  confirmationFormId: id
}: PrevNextLectureProps) => {
  const { data, isLoading } = useQuery(
    ['CUSTOMERDETAIL', personId, id],
    () => getCustomerDetail({ personId, id }),
    { enabled: !!personId && !!id }
  );

  if (isLoading || !data || data.registeredLectures.length === 0) return null;

  const { prevLecture, nextLecture } = getLectureInfo(
    round,
    data.registeredLectures
  );

  const hasPrev = !!prevLecture;
  const hasNext = !!nextLecture;

  return (
    <Container
      className={`flex items-center mt-12 ${
        hasPrev && hasNext
          ? 'justify-between'
          : hasPrev
          ? 'justify-start'
          : hasNext
          ? 'justify-end'
          : ''
      }`}
    >
      {hasPrev && (
        <Item
          label="직전"
          monthDay={prevLecture.endedAt}
          time={prevLecture.endedAt}
        />
      )}
      {hasNext && (
        <Item
          label="다음"
          monthDay={nextLecture.taughtAt}
          time={nextLecture.taughtAt}
        />
      )}
    </Container>
  );
};

const getLectureInfo = (
  round: number,
  registeredLectures: IRegisteredLecture[]
) => {
  const prevLecture = registeredLectures.find(
    lecture => lecture.round === round - 1
  );

  const nextLecture = registeredLectures.find(
    lecture => lecture.round === round + 1
  );

  return { prevLecture, nextLecture };
};

interface ItemProps {
  label: string;
  monthDay: string;
  time: string;
}

const Item = ({ label, monthDay, time }: ItemProps) => {
  const formattedMonthDay = formatMonthDay(monthDay);
  const formattedTime = formatTime(time);

  return (
    <div className="flex justify-center gap-16">
      <Key>{label}</Key>
      <Date>{formattedMonthDay}</Date>
      <Date>{formattedTime}</Date>
    </div>
  );
};

const formatMonthDay = (isoString: string) => {
  return dayjs(isoString).format('M/D');
};

const formatTime = (isoString: string) => {
  if (!isoString) return '시간 미정';
  const date = dayjs(isoString);
  return date.isValid() ? date.format('HH:mm') : '시간 미정';
};

const Container = styled.div`
  width: 100%;
`;

const Date = styled.div`
  color: var(--Gray-Scale-500, #948f9e);
  font-family: 'Pretendard Variable';
  font-size: 16px;
  font-weight: 500;
  line-height: 21px;
  letter-spacing: -0.32px;
`;

const Key = styled.div`
  color: var(--Brand-500, #6238dd);
  font-family: 'Pretendard Variable';
  font-size: 16px;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: -0.32px;
`;

export default PrevNextLecture;
